import { motion } from "framer-motion";
import { CheckCircle, ArrowRight, Percent } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../../Components/Header";
import Footer from "../../Components/Footer";

export default function PricingPage() {
  const plans = [
    {
      title: "Starter",
      original: 1599,
      sale: 499,
      description:
        "For students and freshers who want a credible LinkedIn presence that actually gets noticed.",
      features: [
        "Profile review & actionable audit",
        "Core LinkedIn optimization",
        "1 resume feedback session",
        "1:1 consultation call",
        "Email support",
        "Call Support",
        "2-3 real industry Job Refer",
      ],
      highlight: false,
    },
    {
      title: "Professional",
      original: 2599,
      sale: 999,
      description:
        "For working professionals targeting more recruiter views, better matches, and interview calls.",
      features: [
        "Work Profile + Job Portal rewrite (end-to-end)",
        "ATS-optimized resume revamp",
        "Recruiter keyword targeting",
        "1:1 consultation call",
        "3-6 real industry Job Refer",
        "Email Support",
        "Call Support",
      ],
      highlight: true, // most popular
    },
    {
      title: "Elite",
      original: 2999,
      sale: 1499,
      description:
        "For senior roles—powerful positioning, Job Refer,Mock Interview with real industry expert and ready-to-send collateral.",
      features: [
        "Full LinkedIn + Resume redesign",
        "Custom Cover Letter + Bio",
        "1:1 strategy session ",
        "Recruiter outreach templates",
        "5-10 real industry Job Refer",
        "Lifetime optimization support",
        "Email Support",
        "Call Support",
      ],
      highlight: false,
    },
  ];

  const money = (n) => `₹${n.toLocaleString("en-IN")}`;

  return (
    <div className="bg-gray-50 min-h-screen text-gray-900">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-600 to-purple-700 text-white py-28 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mx-auto px-6"
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase bg-white/15 rounded-full px-3 py-1">
            <Percent size={14} /> Limited-time launch pricing
          </span>
          <h1 className="text-5xl font-extrabold mb-4 mt-4">
            Choose Your Perfect Plan
          </h1>
          <p className="text-lg text-indigo-100 mb-8">
            Transparent pricing. Tangible outcomes. Invest in your next career
            move.
          </p>
          <Link
            to="/contactUs"
            className="inline-flex items-center bg-white text-indigo-700 font-semibold px-6 py-3 rounded-xl shadow hover:bg-indigo-50 transition"
          >
            Get Started <ArrowRight className="ml-2" />
          </Link>
        </motion.div>

        {/* Background Animated Blobs */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute top-20 left-1/4 w-64 h-64 bg-purple-400/40 rounded-full mix-blend-overlay blur-3xl"
            animate={{ y: [0, 20, 0], scale: [1, 1.05, 1] }}
            transition={{ repeat: Infinity, duration: 6 }}
          />
          <motion.div
            className="absolute bottom-10 right-1/3 w-72 h-72 bg-indigo-400/30 rounded-full mix-blend-overlay blur-3xl"
            animate={{ y: [0, -20, 0], scale: [1, 1.05, 1] }}
            transition={{ repeat: Infinity, duration: 7 }}
          />
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 px-6 max-w-7xl mx-auto grid gap-10 md:grid-cols-3">
        {plans.map((plan, idx) => {
          const savings = plan.original - plan.sale;
          const savePct = Math.round((savings / plan.original) * 100);

          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: idx * 0.15, duration: 0.6 }}
              className={`relative bg-white/80 backdrop-blur-lg border rounded-3xl p-8 shadow-xl hover:shadow-2xl transition transform hover:-translate-y-2 ${
                plan.highlight
                  ? "border-indigo-500 ring-2 ring-indigo-500"
                  : "border-gray-200"
              }`}
            >
              {/* Most Popular flag */}
              {plan.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs uppercase tracking-wider px-4 py-1 rounded-full shadow-md">
                  Most Popular
                </div>
              )}

              {/* Limited-time badge */}
              <div className="mb-3">
                <span className="inline-flex items-center gap-2 text-[10px] font-semibold tracking-wider uppercase bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">
                  <Percent size={12} /> Limited-time offer
                </span>
              </div>

              <h3 className="text-2xl font-bold mb-2">{plan.title}</h3>
              <p className="text-gray-500 mb-5">{plan.description}</p>

              {/* Price block */}
              <div className="mb-6">
                <div className="flex items-end justify-center gap-2">
                  <span className="text-xl text-gray-400 line-through">
                    {money(plan.original)}
                  </span>
                  <motion.span
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 220, damping: 18 }}
                    className="text-4xl font-extrabold text-indigo-600"
                  >
                    {money(plan.sale)}
                  </motion.span>
                </div>
                <div className="text-xs text-gray-400 mt-1">/ one-time</div>

                {/* Savings chip */}
                <div className="mt-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
                    You save {money(savings)} ({savePct}%)
                  </span>
                </div>
              </div>

              <ul className="space-y-3 mb-8 text-left">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start text-gray-700">
                    <CheckCircle
                      className="text-green-500 mr-2 mt-1 shrink-0"
                      size={18}
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Link to="/contactUs">
                <button
                  className={`w-full py-3 rounded-xl font-semibold transition ${
                    plan.highlight
                      ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg"
                      : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                  }`}
                >
                  Get Started
                </button>
              </Link>

              {/* Corner ribbon */}
              <div className="pointer-events-none absolute -right-6 -top-6 rotate-12">
                <div
                  className={`px-3 py-1 text-[10px] font-bold rounded shadow-md ${
                    plan.highlight
                      ? "bg-purple-600 text-white"
                      : "bg-gray-900 text-white"
                  }`}
                >
                  {savePct}% OFF
                </div>
              </div>
            </motion.div>
          );
        })}
      </section>

      {/* Trust strip */}
      <section className="bg-gray-100 py-16">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Loved by professionals across industries 💼
          </h2>
          <div className="flex flex-wrap justify-center gap-8 text-gray-700">
            {[
              "IT Professionals",
              "Students",
              "Managers",
              "Designers",
              "Analysts",
            ].map((role, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white/80 backdrop-blur-lg px-6 py-3 rounded-full shadow"
              >
                ⭐ {role}
              </motion.div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-6">
            One-time payment. No hidden fees. GST invoicing available.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
